import { ColumnConfig, TableBody } from '@components/design/migrated/Table';
import CategoryLeaderboardTableRow from '../category-leaderboard-table-row/CategoryLeaderboardTableRow';
import {
  CategoryAndSegmentsLeaderboardGroupDetails,
  CategoryLeaderboardPg,
  GroupState,
} from '@models/CategoryLeaderboard';
import { Filters, PageName, PageType, ShareType } from '@utils/pageConfig/Types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import React, { useState } from 'react';
import * as styles from '@utils/tableViewscss/TableViewBoard.module.scss';
import {
  useCategoryLeaderboardGroupState,
  useFeatureLeaderboardGroupState,
} from '@utils/hook/userPreferences/UseGroupState';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '../utils/CategoryOrSegmentsLeaderboardGroupContext';
import classnames from 'classnames';
import {
  CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
  groupElementClickedTrackingFunction,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { EXPLORE_LEADERBOARD_TABLE, GroupContext } from '@utils/userPreferencesUtils/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getGrandTotalValue } from '@utils/TotalUtils';

interface Props {
  groupDetails: CategoryAndSegmentsLeaderboardGroupDetails;
  categoryLeaderboardPgs: CategoryLeaderboardPg[];
  countries: Filters;
  currencySymbol: string;
  columns: ColumnConfig[];
  sortedColumn: string;
  selectedShareType: ShareType;
  selectedGrowthType: GrowthTypes;
  context: GroupContext;
  isExplorePage?: boolean;
}

/**
 * @return TableBody with nested CategoryLeaderboardTableRow component to display TOTAL and
 * each product group kpis
 *
 * @remarks
 * this component return a row of Group and its total kpis,
 * and also multiple product groups under Group row when row expand button icon is clicked
 *
 * @param groupDetails - details of the group
 * @param categoryLeaderboardPgs - single group kpis
 * @param countries - Filters country
 * @param currencySymbol - Currency symbol
 * @param columns - table columns
 * @param sortedColumn - current sorted column
 * @param selectedShareType
 * @param selectedGrowthType
 * @param context - contains retailer client and country
 * @param isExplorePage - is current page explore page
 * **/
export const CategoryLeaderboardGroupTableRow: React.FC<Props> = ({
  groupDetails,
  categoryLeaderboardPgs,
  countries,
  currencySymbol,
  columns,
  sortedColumn,
  selectedShareType,
  selectedGrowthType,
  context,
  isExplorePage,
}) => {
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const { updateCategoryLeaderboardGroup } = useCategoryLeaderboardGroupState(context);
  const { updateFeatureLeaderboardGroup } = useFeatureLeaderboardGroupState(context);
  const { isEditGroupMode, setIsEditGroupMode, currentSelectedFeature } =
    useCategoryOrSegmentsLeaderboardGroupEventState();
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);

  const CURRENT_TOTAL_NAME = getGrandTotalValue();

  const getGroupTotalKpi: CategoryLeaderboardPg | undefined = categoryLeaderboardPgs.find(
    (categoryLeaderboardPg) => categoryLeaderboardPg.segmentOrPg === CURRENT_TOTAL_NAME,
  );

  const getProductGroupsKpi: CategoryLeaderboardPg[] = categoryLeaderboardPgs.filter(
    (categoryLeaderboardPg) => categoryLeaderboardPg.segmentOrPg !== CURRENT_TOTAL_NAME,
  );

  const getCategoryLeaderboardPgWithGroupName = (): CategoryLeaderboardPg => {
    const displayGroupNameWithPgsCount = `${groupDetails.groupName} (${getProductGroupsKpi.length})`;

    return { ...getGroupTotalKpi!, segmentOrPg: displayGroupNameWithPgsCount };
  };

  const isGroupEmpty: boolean = getProductGroupsKpi.length === 0;

  const constructAndUpdateCategoryLeaderboardGroup = (pgToBeRemove: string) => {
    updateCategoryLeaderboardGroup.mutate({
      key: groupDetails.groupName,
      data: {
        productGroup: groupDetails.segmentOrPgValue.filter((pg) => pg !== pgToBeRemove),
        showPerformanceWithin: currentSelectedFeature.feature,
        sortOrder: groupDetails.sortOrder!,
      },
    });
  };

  const constructAndUpdateFeatureLeaderboardGroup = (pgToBeRemove: string) => {
    updateFeatureLeaderboardGroup.mutate({
      key: groupDetails.groupName,
      data: {
        value: groupDetails.segmentOrPgValue.filter((pg) => pg !== pgToBeRemove),
        showPerformanceWithin: currentSelectedFeature.feature,
        sortOrder: groupDetails.sortOrder!,
      },
    });
  };

  //Delete Group's product group
  const onDeleteGroupPgs = (pgToBeRemove: string) => {
    //Amplitude Tracking when Remove pgs icon is clicked
    const trackingParam: TableElementClickedParam = {
      eventTitle: CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Delete ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: pgToBeRemove,
      pageName: PageName.EXPLORE,
      pageType: PageType.EXPLORE,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: EXPLORE_LEADERBOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    currentSelectedFeature === null || currentSelectedFeature.feature === 'Category'
      ? constructAndUpdateCategoryLeaderboardGroup(pgToBeRemove)
      : constructAndUpdateFeatureLeaderboardGroup(pgToBeRemove);
  };

  const groupStateForGroupHeader: GroupState = {
    groupName: groupDetails.groupName,
    enableIsEditGroupMode: () =>
      setIsEditGroupMode({
        groupDetails: groupDetails,
        isEditMode: true,
      }),
    isGroupingHeader: true,
    isGroupEmpty: isGroupEmpty,
    setIsGroupingRowExpanded: setIsRowExpanded,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };

  const groupStateForGroupBody: GroupState = {
    groupName: groupDetails.groupName,
    isGroupingProductGroup: true,
    onDeleteGroupPgs: onDeleteGroupPgs,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };

  const isDisableGroups = () => {
    return isEditGroupMode.isEditMode && isEditGroupMode.groupDetails.groupName !== groupDetails.groupName;
  };

  return (
    <TableBody>
      {getCategoryLeaderboardPgWithGroupName() && (
        <CategoryLeaderboardTableRow
          key={groupDetails.groupName}
          className={classnames([
            styles.TableViewboard__TableRow__WithHover,
            { [styles['TableViewboard__TableRow__isDisabled']]: isDisableGroups() },
          ])}
          categoryLeaderboardPg={getCategoryLeaderboardPgWithGroupName()}
          columns={columns}
          sortedColumn={sortedColumn}
          selectedGrowthType={selectedGrowthType}
          countries={countries}
          currencySymbol={currencySymbol}
          selectedShareType={selectedShareType}
          groupState={groupStateForGroupHeader}
          isExplorePage={isExplorePage}
        ></CategoryLeaderboardTableRow>
      )}
      {isRowExpanded &&
        !isEditGroupMode.isEditMode &&
        getProductGroupsKpi.map((productGroupsKpi) => (
          <CategoryLeaderboardTableRow
            key={`${groupDetails.groupName} ${productGroupsKpi.segmentOrPg}`}
            className={styles.TableViewboard__TableRow__WithHover}
            categoryLeaderboardPg={productGroupsKpi}
            columns={columns}
            sortedColumn={sortedColumn}
            selectedGrowthType={selectedGrowthType}
            countries={countries}
            currencySymbol={currencySymbol}
            selectedShareType={selectedShareType}
            groupState={groupStateForGroupBody}
            isExplorePage={isExplorePage}
            isPreferenceOwner={groupDetails.isPreferenceOwner}
          ></CategoryLeaderboardTableRow>
        ))}
    </TableBody>
  );
};
